/* eslint-disable react-hooks/exhaustive-deps */

import {
  React, useState, useContext, useEffect,
} from "react";

import ClayButton from "@clayui/button";
import ClayIcon from "@clayui/icon";
import ClayLabel from "@clayui/label";
import ClayLink from "@clayui/link";
import ClayLoadingIndicator from "@clayui/loading-indicator";
import ClayModal, { useModal } from "@clayui/modal";
import { ClaySelect } from "@clayui/form";

import Calendar from "../Form/CustomCalendar";

import { translate } from "../../i18n";

import ApiClient from "../../utils/ApiClient";
import Liferay from "../../utils/Liferay";

import WizardContext from "./context/WizardContext";

/**
 * Componente principal de registro de usuario.
 *
 * @returns Component react.
 */
function ScheduleSelection() {

  // ======================
  // Estados del componente
  // ======================

  const [loading, setLoading] = useState(true);

  const [mode, setMode] = useState("");
  const [doctor, setDoctor] = useState([]);
  const [userTimeZone, setUserTimeZone] = useState();
  const [doctorTimeZone, setDoctorTimeZone] = useState();
  const [selectedOffice, setSelectedOffice] = useState({});
  const [isDoctorFollowed, setIsDoctorFollowed] = useState(false);
  const [doctorAppointmentsConfiguration, setDoctorAppointmentsConfiguration] = useState([]);

  // =======================
  // Hook para uso del modal
  // =======================

  const { observer, onOpenChange, open } = useModal();

  // ===============
  // Uso de contexto
  // ===============

  const {
    extraInfo,
    setStep,
    setExtraInfo,
    setLocalSlot,
    setAppointmentData,
  } = useContext(WizardContext);

  // ========================
  // Funciones del componente
  // ========================

  const uniqueConsultationTypes = Array.from(
    new Set(doctorAppointmentsConfiguration.map((item) => item.consultationType.key)),
  );

  const handleModeChangeHelper = (
    localMode,
    appointmentConfig = doctorAppointmentsConfiguration,
  ) => {

    const offices = [];

    if (localMode === "online") {

      for (let index = 0; index < appointmentConfig.length; index += 1) {

        if (appointmentConfig[index].consultationType?.key === localMode) {

          offices.push(appointmentConfig[index]);

        }

      }

    } else {

      for (let index = 0; index < appointmentConfig.length; index += 1) {

        if (appointmentConfig[index].consultationType?.key === localMode
          && (appointmentConfig[index].r_officeAgendaConfiguration_c_doctorsOfficeId
            === appointmentConfig[0].r_officeAgendaConfiguration_c_doctorsOfficeId
            || offices.length === 0)) {

          offices.push(appointmentConfig[index]);

        }

      }

      offices.sort((a, b) => a.startTime - b.startTime);

    }

    setAppointmentData(offices[0]);

    setSelectedOffice(offices);

    setMode(localMode);

    setExtraInfo({
      ...extraInfo,
      consultationMode: localMode,
      office: offices,
    });

  };

  const handleModeChange = (event) => {

    handleModeChangeHelper(event.target?.value || event);

  };

  const handleBlockChange = (event, appointmentConfig = doctorAppointmentsConfiguration) => {

    const filteredValue = appointmentConfig
      .filter((config) => (config.r_officeAgendaConfiguration_c_doctorsOfficeId
        === parseInt(event.target?.value || event, 10)));

    setAppointmentData(filteredValue[0]);

    filteredValue.sort((a, b) => a.startTime - b.startTime);

    setSelectedOffice(filteredValue);

    setExtraInfo({
      ...extraInfo,
      office: filteredValue,
    });

  };

  /**
   * Evalúa si el doctor es seguido por el usuario con sesión activa.
   *
   * @param userId - Id del usuario a evaluar.
   *
   * @returns Respuesta del servicio.
   */
  async function isFollowed(userId) {

    if (Liferay.ThemeDisplay.isSignedIn()) {

      return ApiClient.doctorIsFollowed(userId);

    }

    return [];

  }

  /**
   * Permite seguir a un doctor.
   *
   * @param userId - Id del usuario a seguir.
   */
  async function followDoctor(userId) {

    await ApiClient.followDoctor(userId);

  }

  /**
   * Permite dejar de seguir a un doctor.
   *
   * @param userId - Id del usuario a dejar de seguir.
   */
  async function unfollowDoctor(userId) {

    await ApiClient.unfollowDoctor(userId);

  }

  /**
   * Carga los doctores seguidos.
   */
  async function getDoctorData() {

    try {

      const searchParams = new URLSearchParams(window.location.search);
      const doctorId = searchParams.get("doctorId");

      const resp = await ApiClient.getDoctor(doctorId || "");

      setDoctor(resp.data);

      let myUser;

      if (!Liferay.ThemeDisplay.isSignedIn()) {

        onOpenChange(true);

        throw String("USER_NOT_LOGGED");

      } else {

        if ((window.location.href).includes("dr-now-public")) {

          Liferay.SPA.app.navigate(
            `/web/dr-now-user-private/appointment-creation?doctorId=${doctorId}`,
          );

        }

        myUser = (await ApiClient.geMyLiferayUser()).data;

        myUser.drNowUser = (await ApiClient.geMyDrNowUser()).data.items;

      }

      const hasConnectionResponse = await isFollowed(resp.data.drNowDoctor.id);

      setIsDoctorFollowed(hasConnectionResponse.data?.hasConnection);

      if (resp.data.agendaConfigurations.length === 0
        || resp.data.medicalAppointmentDetails.length === 0) {

        throw String("AGENDA_CONFIGURATION_NOT_FOUND");

      } else {

        setUserTimeZone(await ApiClient.getUserTimeZone());

        setDoctorAppointmentsConfiguration(resp.data.agendaConfigurations);

        setDoctorTimeZone(await ApiClient.getAnyUserTimeZone(resp.data.drNowDoctor.id));

        setAppointmentData(resp.data.agendaConfigurations[0]);

        if (extraInfo.consultationMode && extraInfo.office) {

          handleModeChangeHelper(extraInfo.consultationMode, resp.data.agendaConfigurations);

          handleBlockChange(
            extraInfo.office[0].r_officeAgendaConfiguration_c_doctorsOfficeId,
            resp.data.agendaConfigurations,
          );

          setExtraInfo({
            ...extraInfo,
          });

        } else {

          handleModeChangeHelper(
            resp.data.agendaConfigurations[0].consultationType?.key,
            resp.data.agendaConfigurations,
          );

          setExtraInfo({
            ...extraInfo,
            user: myUser,
            doctorData: resp.data,
            price: resp.data.medicalAppointmentDetails[0]?.firstConsultationPrice,
            onlinePrice: resp.data.medicalAppointmentDetails[0]?.followUpConsultationPrice,
            appointmentPrice: resp.data.medicalAppointmentDetails[0]?.firstConsultationPrice,
            consultationMode: resp.data.agendaConfigurations[0].consultationType?.key,
            currency: resp.data.bankAccounts[0].currency,
          });

        }

      }

    } catch (error) {

      if (error !== "AGENDA_CONFIGURATION_NOT_FOUND"
        && error !== "USER_NOT_LOGGED") {

        Liferay.Util.openToast({
          title: "",
          message: `
            <h4 class='font-weight-medium m-0'>
              ${translate("service-error")}
            </h4>
            <span class='text-3'>
              ${translate("please-try-again-later")}
            </span>
          `,
          type: "danger",
          toastProps: {
            hideCloseIcon: true,
          },
        });

      }

    } finally {

      setLoading(false);

    }

  }

  /**
   * Carga la estructura del modal de crear perfil.
   *
   * @returns Estructura del modal de crear perfil.
   */
  function renderModal() {

    if (!open) {

      return false;

    }

    return (
      <ClayModal className="overflow-hidden" center disableAutoClose observer={observer} size="lg">

        <ClayModal.Body>

          <div className="d-flex flex-column text-center">

            <div className="d-flex justify-content-end mt-3">

              <ClayButton
                aria-label="close"
                className="close"
                displayType="unstyled"
                onClick={() => {

                  Liferay.SPA.app.navigate("/web/dr-now-public/home");

                  onOpenChange(false);

                }}
              >
                <ClayIcon spritemap={Liferay.Icons.spritemap} symbol="times" />
              </ClayButton>

            </div>

            <div className="mt-2">

              <ClayButton
                displayType="primary text-3"
                size="lg"
                onClick={() => {

                  onOpenChange(false);
                  Liferay.SPA.app.navigate("/web/dr-now-public/sign-in");

                }}
              >
                {translate("log-in")}
              </ClayButton>

            </div>

            <span className="mt-4 text-3">
              {translate("register-now")}
            </span>

            <div className="mt-4 mb-2">

              <ClayButton
                className="text-primary text-3"
                displayType="secondary"
                size="lg"
                onClick={() => {

                  onOpenChange(false);
                  Liferay.SPA.app.navigate("/web/dr-now-public/sign-up");

                }}
              >
                {translate("register")}
              </ClayButton>

            </div>

          </div>

        </ClayModal.Body>

      </ClayModal>
    );

  }

  /**
   * Renderiza botones en card con datos del médico.
   *
   * @param classes - Clases CSS adicionales.
   *
   * @returns Botones.
   */
  function renderButtons(classes) {

    return (
      <div className={`row mt-2 ${classes}`}>

        <div className="col-6 col-lg-auto pr-1">

          {isDoctorFollowed ? (

            <ClayButton
              block
              size="sm"
              className="bg-light border-primary"
              onClick={() => {

                setIsDoctorFollowed(false);
                unfollowDoctor(doctor.drNowDoctor.id);

              }}
            >
              <ClayIcon
                symbol="dr-now-user-plus"
                className="text-primary mr-2"
                spritemap={Liferay.Icons.spritemap}
              />

              <span className="text-primary">
                {translate("followed")}
              </span>

            </ClayButton>

          ) : (

            <ClayButton
              block
              outline
              size="sm"
              displayType="secondary"
              className="text-dark border-dark"
              onClick={() => {

                setIsDoctorFollowed(true);
                followDoctor(doctor.drNowDoctor.id);

              }}
            >

              {translate("follow")}

            </ClayButton>

          )}

        </div>

        <div className="col-6 col-lg-auto mt-lg-0 pl-1">

          <ClayButton
            block
            outline
            size="sm"
            displayType="secondary"
            className="text-dark border-dark"
            onClick={() => {

              const currentUrl = window.location.href;

              if (currentUrl.includes("dr-now-public")) {

                Liferay.SPA.app.navigate(`/web/dr-now-public/l/${doctor.id}`);

              } else {

                Liferay.SPA.app.navigate(`/web/dr-now-user-private/l/${doctor.id}`);

              }

            }}
          >

            <span className="d-none d-md-block">
              {translate("complete-profile")}
            </span>

            <span className="d-block d-md-none">
              {translate("complete-profile-short")}
            </span>

          </ClayButton>

        </div>

      </div>
    );

  }

  /**
   * Renderiza el header de cada cita.
   *
   * @returns Componente React.
   */
  function renderHeaderContent() {

    return (
      <div className="row">

        <div className="col-auto">

          <img
            alt="thumbnail"
            className="rounded-lg"
            style={{ height: "7rem", width: "7rem" }}
            src={doctor.offices[0].creator.image}
          />

        </div>

        <div className="col pl-0">

          <div className="row">
            <div className="col">

              <h5 className="text-primary font-weight-bold">
                {`${doctor.prefix?.name} ${doctor.name}`}
              </h5>

            </div>
          </div>

          <div className="row">
            <div className="col">

              <h6 className="font-weight-semi-bold">
                {doctor.medicalSpecialities.map((spec) => spec.name).join(" - ")}
              </h6>

            </div>
          </div>

          <div className="row mt-1">

            <div className="col-auto d-none d-lg-block pr-0">

              <span className="text-2 font-weight-semi-bold">
                {`${translate("meet")}:`}
              </span>

            </div>

            <div className="col-auto pl-md-1">

              {uniqueConsultationTypes.map((meet) => (

                <ClayLabel
                  displayType="secondary"
                  className="bg-primary-l5 text-1 text-light font-weight-semi-bold"
                >

                  {meet === "inPerson" ? (translate("in-person")) : (translate("online"))}

                </ClayLabel>

              ))}

            </div>
          </div>

          {renderButtons("d-none d-md-flex")}

        </div>

      </div>
    );

  }

  /**
   * Renderiza el cuerpo de cada cita.
   *
   * @returns Componente React.
   */
  function renderBodyContent() {

    return (
      <>
        <div className="row">

          <div className="col-12 col-lg-4">

            <span className="text-3 font-weight-semi-bold">
              {translate("select-appointment-type")}
            </span>

            <ClaySelect
              aria-label="Select Label"
              id="mySelectId"
              onChange={handleModeChange}
              defaultValue={mode}
            >

              {doctorAppointmentsConfiguration
                .find((appointment) => appointment.consultationType?.key === "inPerson")
                  !== undefined ? (

                    <ClaySelect.Option
                      key="inPerson"
                      label={translate("in-person")}
                      value="inPerson"
                    />

                ) : (

                  ""

                )}

              {doctorAppointmentsConfiguration
                .find((appointment) => appointment.consultationType?.key === "online")
                  !== undefined ? (

                    <ClaySelect.Option
                      key="online"
                      label={translate("online")}
                      value="online"
                      selected={mode === "online"}
                    />

                ) : (

                  ""

                )}

            </ClaySelect>

          </div>

          {mode !== "online" ? (

            <div className="col-12 col-lg-7 d-flex flex-column pt-3 pt-md-0">

              <span className="text-3 font-weight-semi-bold">
                {translate("selected-consultory")}
              </span>

              <ClaySelect
                aria-label="Select Label"
                id="mySelectId"
                onChange={handleBlockChange}
                defaultValue={selectedOffice[0]?.r_officeAgendaConfiguration_c_doctorsOfficeId}
              >

                {(() => {

                  let cummulated = 0;
                  const choosedConfigs = [];

                  return doctorAppointmentsConfiguration.reduce((options, office) => {

                    if (office.consultationType?.key === mode
                        && (choosedConfigs.find((config) => config
                          === office.r_officeAgendaConfiguration_c_doctorsOfficeId) === undefined
                          || choosedConfigs.length === 0)) {

                      cummulated += 1;
                      choosedConfigs.push(office.r_officeAgendaConfiguration_c_doctorsOfficeId);

                      options.push(

                        <ClaySelect.Option
                          key={office.r_officeAgendaConfiguration_c_doctorsOfficeId}
                          label={
                              office?.r_officeAgendaConfiguration_c_doctorsOffice?.address
                              || `${translate("online")} ${cummulated}`
                            }
                          value={office.r_officeAgendaConfiguration_c_doctorsOfficeId}
                        />,

                      );

                    }
                    return options;

                  }, []);

                })()}

              </ClaySelect>

            </div>

          ) : (

            ""

          )}

        </div>

        <div className="row mt-3 mb-3 mb-lg-0">

          {
            mode !== "online" ? (

              <div className="col-12 col-lg">

                <div className="row">

                  {selectedOffice[0]?.consultationType?.key === "inPerson" ? (

                    <>

                      <div className="col-6 col-lg-3">

                        <div className="row">
                          <div className="col">

                            <span className="text-3 font-weight-semi-bold">
                              {`${translate("country")}`}
                              :
                            </span>

                          </div>
                        </div>

                        <div className="row mt-n2">
                          <div className="col">

                            <span className="text-3 font-weight-light">

                              {selectedOffice[0]
                                ?.r_officeAgendaConfiguration_c_doctorsOffice.countryName}

                            </span>

                          </div>
                        </div>

                      </div>

                      <div className="col-6 col-lg-3">

                        <div className="row">
                          <div className="col">

                            <span className="text-3 font-weight-semi-bold">
                              {`${translate("state/city")}`}
                              :
                            </span>

                          </div>
                        </div>

                        <div className="row mt-n2">
                          <div className="col">

                            <span className="text-3 font-weight-light text-capitalize">

                              {selectedOffice[0]
                                ?.r_officeAgendaConfiguration_c_doctorsOffice.city}

                            </span>

                          </div>
                        </div>

                      </div>

                      <div className="col-12 col-lg-3 pt-2 pt-md-0">

                        <div className="row">
                          <div className="col">

                            <span className="text-3 font-weight-semi-bold">
                              {`${translate("address")}`}
                              :
                            </span>

                          </div>
                        </div>

                        <div className="row mt-n2">
                          <div className="col">

                            <span className="text-3 font-weight-light">

                              {selectedOffice[0]
                                ?.r_officeAgendaConfiguration_c_doctorsOffice.address}

                            </span>

                          </div>
                        </div>

                      </div>

                      <div className="col-12 col-lg-3 pt-2 pt-md-0">

                        <div className="row font-weight-semi-bold">
                          <div className="col">

                            <span className="text-3">
                              {`${translate("consultation-value-simple")}`}
                              :
                            </span>

                          </div>
                        </div>

                        <div className="row mt-n2">
                          <div className="col-auto">

                            <span className="text-primary text-3 font-weight-semi-bold">
                              {doctor.medicalAppointmentDetails[0]?.firstConsultationPrice}
                            </span>

                            {doctor.bankAccounts.length > 0 && (

                            <span className="text-primary font-weight-semi-bold ml-1">
                              {doctor.bankAccounts[0].currency.toUpperCase()}
                            </span>

                            )}

                          </div>
                        </div>

                      </div>

                    </>

                  ) : (

                    ""

                  )}

                </div>

              </div>

            ) : (

              <div className="col-12 col-lg-3 pt-2 pt-md-0">

                <div className="row font-weight-semi-bold">
                  <div className="col">

                    <span className="text-3">
                      {`${translate("consultation-value-simple")}`}
                      :
                    </span>

                  </div>
                </div>

                <div className="row mt-n2">
                  <div className="col-auto">

                    <span className="text-primary font-weight-semi-bold text-3">
                      {doctor.medicalAppointmentDetails[0]?.followUpConsultationPrice}
                    </span>

                    {doctor.bankAccounts.length > 0 && (

                    <span className="text-primary font-weight-semi-bold ml-1">
                      {doctor.bankAccounts[0].currency.toUpperCase()}
                    </span>

                    )}

                  </div>
                </div>

              </div>

            )
          }

        </div>

      </>
    );

  }

  /**
   * Renderiza tarjeta de cita.
   *
   * @returns Componente React.
   */
  function renderContent() {

    return (
      <div className="border shadow rounded-lg p-0 p-sm-3 dr-now-appointment-container">

        <div className="row">
          <div className="col-12 col-md">

            <div className="mb-3">

              {renderHeaderContent()}

              {renderButtons("d-flex d-md-none mt-3")}

            </div>

            <div>

              {
              Liferay.ThemeDisplay.isSignedIn() && doctorAppointmentsConfiguration.length !== 0 ? (

                renderBodyContent()

              ) : (

                ""

              )
            }

            </div>

          </div>

          <div className="col-12 col-md px-0 pt-2 pt-md-0 dr-now-calendar-container">

            {
            doctorAppointmentsConfiguration.length > 0 && doctor.agendaAvailable ? (

              <Calendar
                doctorTimeZone={doctorTimeZone}
                userTimeZone={userTimeZone}
                appointments={doctor.medicalAppointments}
                schedule={selectedOffice}
                setLocalSlot={setLocalSlot}
                setStep={setStep}
              />

            ) : (

              <Calendar
                userTimeZone="America/Panama"
                appointments={[]}
                schedule={[{
                  days: [],
                  startTime: 7,
                  endTime: 12,
                  appointmentDuration: { key: "30" },
                }]}
                setLocalSlot={setLocalSlot}
                setStep={setStep}
              />

            )
          }

          </div>

        </div>

      </div>
    );

  }

  // ========================
  // Función de carga inicial
  // ========================

  useEffect(() => {

    if (!Liferay || !Liferay.SPA) {

      return;

    }

    getDoctorData();

  }, []);

  // =======================
  // Template del componente
  // =======================

  return (

    <div className="col">

      {
        !loading ? (

          <div>

            <div className="row">
              <div className="col">

                <ClayLink
                  decoration="none"
                  displayType="unstyled"
                  onClick={() => Liferay.SPA.app.navigate("/web/dr-now-user-private/home")}
                >

                  <ClayIcon
                    className="mr-2"
                    spritemap={Liferay.Icons.spritemap}
                    symbol="dr-now-arrow-left"
                  />

                  <span className="text-primary text-3 font-weight-semi-bold">
                    {translate("return-home")}
                  </span>

                </ClayLink>

              </div>
            </div>

            <div className="row my-3">
              <div className="col">

                {renderContent()}

              </div>
            </div>

            {renderModal()}

          </div>

        ) : (

          <div className="d-flex justify-content-center align-items-center vh-100">
            <ClayLoadingIndicator displayType="primary" shape="squares" size="lg" />
          </div>

        )
      }

    </div>

  );

}

export default ScheduleSelection;
